// Decides whether a downloaded OTA bundle may be swapped in right now, while
// the app is backgrounded. A swap boots a fresh WebView on the same route, so
// anything that lives only in the page — an open dialog, a typed draft, a share
// sheet waiting to resolve — would be lost.

// React 19 keeps `defaultValue` equal to `value` on controlled fields, so
// "differs from its default" can't tell a typed draft from a prefilled field.
// Track the fields the user actually typed into instead.
const typedInto = new WeakSet<EventTarget>()
const holds = new Set<symbol>()
let lastInteractionAt = 0

// A background that follows a click this closely was started by the app itself
// (photo picker, share sheet, external link, OAuth) — the user is coming back
// to a pending result. Pressing Home or switching apps never touches the page.
const APP_INITIATED_WINDOW_MS = 2000

let installed = false

export function installOtaSwapGuards(): void {
  if (installed) return
  installed = true
  document.addEventListener('input', e => { if (e.target) typedInto.add(e.target) }, true)
  const touch = () => { lastInteractionAt = Date.now() }
  // click, not pointerdown: iOS hands the page a pointerdown when the home
  // swipe starts at the bottom edge, then cancels it — every trip home would
  // look app-initiated. A system gesture never completes a click.
  document.addEventListener('click', touch, true)
  document.addEventListener('keydown', touch, true)
}

// Held around flows that send the user out of the app and expect a result back.
// Released when the flow resolves; releaseOtaSwapHolds() drops any left over on
// the next foreground, so a hold never outlives one round trip.
export function holdOtaSwap(): () => void {
  const token = Symbol('ota-hold')
  holds.add(token)
  return () => { holds.delete(token) }
}

export function releaseOtaSwapHolds(): void {
  holds.clear()
}

function isVisible(el: HTMLElement): boolean {
  return el.offsetParent !== null
}

function hasDraft(root: ParentNode): boolean {
  const fields = root.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>('textarea, input:not([type]), input[type="text"], input[type="search"], input[type="url"], input[type="email"]')
  for (const field of fields) {
    if (typedInto.has(field) && field.value.trim() !== '' && isVisible(field)) return true
  }
  return false
}

// Why the page can't be replaced right now, or null when it can. Covers state
// the swap would destroy; used for release bumps on resume as well as the
// background swap.
export function transientStateReason(root: Document = document): string | null {
  if (holds.size > 0) return 'hold'
  if (root.querySelector('[role="dialog"][data-state="open"], [role="alertdialog"][data-state="open"]')) return 'dialog'
  if (hasDraft(root)) return 'draft'
  return null
}

// transientStateReason plus the checks that only make sense at the moment the
// app goes to the background.
export function backgroundSwapBlocker(now = Date.now(), root: Document = document): string | null {
  if (now - lastInteractionAt < APP_INITIATED_WINDOW_MS) return 'app_initiated'
  if (window.location.pathname.startsWith('/login')) return 'login'
  return transientStateReason(root)
}
